import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { UserIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

export const BetaApplicationsPage = () => {
  // Get manuscript ID from URL
  const pathParts = window.location.pathname.split('/');
  const manuscriptId = pathParts[pathParts.length - 2]; // Assuming URL is /writer/manuscripts/:id/applications

  const [manuscriptTitle, setManuscriptTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (manuscriptId) {
      fetchApplications();
    }
  }, [manuscriptId]);

  const formatTimeSince = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const mapReadingSpeed = (speed: string) => {
    const speedMap: Record<string, string> = {
      SLOW: '6-8 weeks',
      MODERATE: '3-4 weeks',
      FAST: '2-3 weeks',
    };
    return speedMap[speed] || '4-6 weeks';
  };

  const mapExperienceLevel = (level: string) => {
    const levelMap: Record<string, string> = {
      BEGINNER: 'New to beta reading',
      INTERMEDIATE: 'Some beta reading experience',
      EXPERIENCED: 'Experienced beta reader',
    };
    return levelMap[level] || 'Not specified';
  };

  const fetchApplications = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.betaApplications.forManuscript(manuscriptId), {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch applications');
        setLoading(false);
        return;
      }

      console.log('[BetaApplicationsPage] Received data from API:', data);

      // Set manuscript title from first application
      if (data.applications.length > 0 && data.applications[0].manuscript) {
        setManuscriptTitle(data.applications[0].manuscript.title);
      }

      const transformedApplications = data.applications.map((app: any) => {
        console.log('[BetaApplicationsPage] Processing app:', {
          id: app.id,
          readingSpeed: app.readingSpeed,
          experienceLevel: app.experienceLevel,
          message: app.message
        });
        // Map backend status to frontend status
        let frontendStatus = 'pending';
        if (app.status === 'PENDING') {
          frontendStatus = 'pending';
        } else if (app.status === 'ACCEPTED') {
          frontendStatus = 'accepted';
        } else if (app.status === 'REJECTED') {
          frontendStatus = 'declined';
        }

        return {
          id: app.id,
          readerId: app.readerId,
          readerName: app.reader?.displayName || 'Unknown Reader',
          readerBio: app.reader?.bio || 'No bio provided',
          profilePhoto: null,
          appliedDate: formatTimeSince(new Date(app.createdAt)),
          status: frontendStatus,
          whyInterested: app.message || 'No message provided',
          estimatedTimeline: mapReadingSpeed(app.readingSpeed),
          previousExperience: mapExperienceLevel(app.experienceLevel),
          preferredGenres: app.reader?.preferredGenres || [],
          completedReads: 0, // Not available from backend yet
          avgRating: null, // Not available from backend yet
        };
      });

      console.log('[BetaApplicationsPage] Transformed applications:', transformedApplications);

      setApplications(transformedApplications);
      setLoading(false);
    } catch (err) {
      console.error('Fetch applications error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const filteredApplications = applications.filter(app =>
    statusFilter === 'all' || app.status === statusFilter
  );

  const handleAccept = async (applicationId: string, readerName: string) => {
    if (window.confirm(`Accept ${readerName} as a beta reader?`)) {
      try {
        const response = await fetch(API_ENDPOINTS.betaApplications.updateStatus(applicationId), {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: 'ACCEPTED' }),
        });

        const data = await response.json();

        if (!response.ok) {
          window.alert(data.error || 'Failed to accept application');
          return;
        }

        setApplications(applications.map(app =>
          app.id === applicationId ? { ...app, status: 'accepted' } : app
        ));
        window.alert(`${readerName} has been accepted! They will be notified via email.`);
      } catch (err) {
        console.error('Accept application error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const handleDecline = async (applicationId: string, readerName: string) => {
    if (window.confirm(`Decline ${readerName}'s application?`)) {
      try {
        const response = await fetch(API_ENDPOINTS.betaApplications.updateStatus(applicationId), {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ status: 'REJECTED' }),
        });

        const data = await response.json();

        if (!response.ok) {
          window.alert(data.error || 'Failed to decline application');
          return;
        }

        setApplications(applications.map(app =>
          app.id === applicationId ? { ...app, status: 'declined' } : app
        ));
        window.alert(`${readerName}'s application has been declined.`);
      } catch (err) {
        console.error('Decline application error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedApplications(filteredApplications.filter(app => app.status === 'pending').map(app => app.id));
    } else {
      setSelectedApplications([]);
    }
  };

  const handleSelectApplication = (applicationId: string) => {
    if (selectedApplications.includes(applicationId)) {
      setSelectedApplications(selectedApplications.filter(id => id !== applicationId));
    } else {
      setSelectedApplications([...selectedApplications, applicationId]);
    }
  };

  const handleBulkAccept = async () => {
    if (selectedApplications.length === 0) {
      window.alert('Please select applications to accept');
      return;
    }
    if (window.confirm(`Accept ${selectedApplications.length} selected application(s)?`)) {
      try {
        // Update each application
        await Promise.all(
          selectedApplications.map(appId =>
            fetch(API_ENDPOINTS.betaApplications.updateStatus(appId), {
              method: 'PATCH',
              headers: getAuthHeaders(),
              body: JSON.stringify({ status: 'ACCEPTED' }),
            })
          )
        );

        setApplications(applications.map(app =>
          selectedApplications.includes(app.id) ? { ...app, status: 'accepted' } : app
        ));
        setSelectedApplications([]);
        window.alert('Selected applications have been accepted!');
      } catch (err) {
        console.error('Bulk accept error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const handleBulkDecline = async () => {
    if (selectedApplications.length === 0) {
      window.alert('Please select applications to decline');
      return;
    }
    if (window.confirm(`Decline ${selectedApplications.length} selected application(s)?`)) {
      try {
        // Update each application
        await Promise.all(
          selectedApplications.map(appId =>
            fetch(API_ENDPOINTS.betaApplications.updateStatus(appId), {
              method: 'PATCH',
              headers: getAuthHeaders(),
              body: JSON.stringify({ status: 'REJECTED' }),
            })
          )
        );

        setApplications(applications.map(app =>
          selectedApplications.includes(app.id) ? { ...app, status: 'declined' } : app
        ));
        setSelectedApplications([]);
        window.alert('Selected applications have been declined.');
      } catch (err) {
        console.error('Bulk decline error:', err);
        window.alert('Network error. Please try again.');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      accepted: { variant: 'success', label: 'Accepted' },
      declined: { variant: 'default', label: 'Declined' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const pendingCount = applications.filter(app => app.status === 'pending').length;
  const acceptedCount = applications.filter(app => app.status === 'accepted').length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '32px',
        }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = `/writer/manuscripts/${manuscriptId}`}
            style={{ marginBottom: '16px' }}
          >
            ← Back to Manuscript
          </Button>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Beta Reader Applications
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
            marginBottom: '12px',
          }}>
            {manuscriptTitle}
          </p>
          <div style={{
            fontSize: '15px',
            color: theme.colors.textTertiary,
            fontFamily: theme.fonts.monospace,
          }}>
            {pendingCount} pending • {acceptedCount} accepted • {applications.length} total
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '60px 40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading applications...
              </p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {!loading && error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.error,
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchApplications}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Filter and Bulk Actions */}
        {!loading && !error && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Applications ({applications.length})</option>
            <option value="pending">Pending ({pendingCount})</option>
            <option value="accepted">Accepted ({acceptedCount})</option>
            <option value="declined">Declined ({applications.filter(app => app.status === 'declined').length})</option>
          </select>

          {/* Bulk Actions */}
          {selectedApplications.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
            }}>
              <span style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
              }}>
                {selectedApplications.length} selected
              </span>
              <Button
                variant="primary"
                size="small"
                onClick={handleBulkAccept}
              >
                Accept Selected
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={handleBulkDecline}
              >
                Decline Selected
              </Button>
            </div>
          )}
        </div>
        )}

        {/* Select All Checkbox */}
        {filteredApplications.filter(app => app.status === 'pending').length > 0 && (
          <Card style={{ marginBottom: '24px', padding: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '15px',
              color: theme.colors.textPrimary,
            }}>
              <input
                type="checkbox"
                checked={selectedApplications.length === filteredApplications.filter(app => app.status === 'pending').length && filteredApplications.filter(app => app.status === 'pending').length > 0}
                onChange={handleSelectAll}
                style={{ marginRight: '12px', width: '18px', height: '18px' }}
              />
              Select all pending applications
            </label>
          </Card>
        )}

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                {statusFilter === 'all'
                  ? 'No applications yet'
                  : `No ${statusFilter} applications`}
              </p>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            {filteredApplications.map((application) => (
              <Card key={application.id}>
                <div style={{
                  display: 'flex',
                  gap: '24px',
                }}>
                  {/* Checkbox for pending applications */}
                  {application.status === 'pending' && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      paddingTop: '4px',
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedApplications.includes(application.id)}
                        onChange={() => handleSelectApplication(application.id)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </div>
                  )}

                  {/* Profile Photo */}
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: theme.colors.primary,
                  }}>
                    <UserIcon width={40} height={40} />
                  </div>

                  {/* Application Details */}
                  <div style={{ flex: 1 }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                      gap: '16px',
                    }}>
                      <div>
                        <h3 style={{
                          fontSize: '22px',
                          fontWeight: '500',
                          marginBottom: '4px',
                          color: theme.colors.textPrimary,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          {application.readerName}
                        </h3>
                        <div style={{
                          fontSize: '14px',
                          color: theme.colors.textTertiary,
                          fontFamily: theme.fonts.monospace,
                        }}>
                          Applied {application.appliedDate}
                        </div>
                      </div>
                      {getStatusBadge(application.status)}
                    </div>

                    {/* Reader Stats */}
                    <div style={{
                      display: 'flex',
                      gap: '24px',
                      marginBottom: '16px',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      fontFamily: theme.fonts.monospace,
                    }}>
                      <span>{application.completedReads} completed reads</span>
                      {application.avgRating && (
                        <>
                          <span>•</span>
                          <span>★ {application.avgRating.toFixed(1)} avg rating</span>
                        </>
                      )}
                    </div>

                    {/* Bio */}
                    <p style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: theme.colors.textPrimary,
                      marginBottom: '16px',
                    }}>
                      {application.readerBio}
                    </p>

                    {/* Preferred Genres */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: theme.colors.textSecondary,
                        marginBottom: '8px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}>
                        Preferred Genres
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                      }}>
                        {application.preferredGenres.map((genre: string, index: number) => (
                          <Badge key={index} variant="primary" size="small">
                            {genre}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Application Details */}
                    <div style={{
                      backgroundColor: theme.colors.backgroundAlt,
                      padding: '16px',
                      borderRadius: theme.borderRadius.medium,
                      marginBottom: '16px',
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: theme.colors.textSecondary,
                          marginBottom: '6px',
                        }}>
                          Why interested:
                        </div>
                        <p style={{
                          fontSize: '15px',
                          lineHeight: '1.6',
                          color: theme.colors.textPrimary,
                        }}>
                          {application.whyInterested}
                        </p>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '16px',
                        fontSize: '14px',
                      }}>
                        <div>
                          <span style={{ color: theme.colors.textSecondary }}>Timeline: </span>
                          <span style={{ color: theme.colors.textPrimary }}>{application.estimatedTimeline}</span>
                        </div>
                        <div>
                          <span style={{ color: theme.colors.textSecondary }}>Experience: </span>
                          <span style={{ color: theme.colors.textPrimary }}>{application.previousExperience}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {application.status === 'pending' && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                      }}>
                        <Button
                          onClick={() => handleAccept(application.id, application.readerName)}
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDecline(application.id, application.readerName)}
                        >
                          Decline
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          View Full Profile
                        </Button>
                      </div>
                    )}

                    {application.status === 'accepted' && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                      }}>
                        <Button
                          variant="outline"
                          onClick={() => window.location.href = `/messages/new?to=${application.readerId}`}
                        >
                          Send Message
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => window.location.href = `/reader/${application.readerId}`}
                        >
                          View Profile
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
